import type { Theme } from "../types/Theme";
import { resolveEmphasisBorderWidth } from "./choiceThemeTokens";
import { alpha, darken, inputBackgroundColor, foregroundColor } from "./Mixins";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";
import { themeNumber } from "../utils";

/** Transparent gap between input border and focus halo (px). */
export const TEXT_INPUT_FOCUS_GAP_PX = 2;

/** Vertical gap between combobox and detached listbox panel (px). */
export const SELECT_LISTBOX_GAP_PX = 4;

/** Listbox corner radius relative to the combobox (px). */
export const SELECT_LISTBOX_RADIUS_OFFSET_PX = 2;

/** Modern listbox option row height (px). */
export const SELECT_LISTBOX_OPTION_HEIGHT = 40;

/** Modern listbox option padding (px): top/bottom, left/right. */
export const SELECT_LISTBOX_OPTION_PADDING_Y = 6;
export const SELECT_LISTBOX_OPTION_PADDING_X = 8;

export function resolveSelectListboxOptionPadding(): number[][] {
    return [[SELECT_LISTBOX_OPTION_PADDING_Y, SELECT_LISTBOX_OPTION_PADDING_X]];
}

/** Globe/caret SVG size in modern combobox (px). */
export const SELECT_COMBOBOX_ICON_SIZE = 16;

/** `selectContent` inset when a left/right accessory is present (px). */
export function resolveSelectComboboxContentPaddingLeft(hasLeftIcon: boolean): number {
    return hasLeftIcon
        ? SELECT_LISTBOX_OPTION_PADDING_X +
              SELECT_COMBOBOX_ICON_SIZE +
              SELECT_LISTBOX_OPTION_PADDING_X
        : SELECT_LISTBOX_OPTION_PADDING_X;
}

export function resolveSelectComboboxContentPaddingRight(hasCaret: boolean): number {
    return hasCaret
        ? SELECT_LISTBOX_OPTION_PADDING_X + SELECT_COMBOBOX_ICON_SIZE
        : SELECT_LISTBOX_OPTION_PADDING_X;
}

export function resolveSelectComboboxAccessoryMargin(): number {
    return SELECT_LISTBOX_OPTION_PADDING_X;
}

export function useModernInputChrome(theme: Theme): boolean {
    return shouldUseModernTheme(theme);
}

export function resolveTextInputPaddingY(theme: Theme): number {
    return theme.TextInputPaddingY ?? 4;
}

export function resolveTextInputPaddingX(theme: Theme): number {
    return theme.TextInputPaddingX ?? 16;
}

export function resolveTextInputPaddingInlineWithAccessory(theme: Theme): number {
    return theme.TextInputPaddingInlineWithAccessory ?? 38;
}

export function resolveTextInputAccessoryMargin(theme: Theme): number {
    return theme.TextInputAccessoryMargin ?? 16;
}

/** Focus halo stroke width on the `::after` ring (px). 0 disables the halo. */
export function resolveTextInputFocusRingStroke(theme: Theme): number {
    return theme.TextInputFocusRingWidth ?? 0;
}

export function resolveTextInputFocusRingOffset(theme: Theme): number {
    return resolveTextInputFocusRingStroke(theme) + TEXT_INPUT_FOCUS_GAP_PX;
}

/** Legacy underline stroke for a transparent, borderless field (px). */
export const FIELD_UNDERLINE_WIDTH_PX = 3;

/** Field border width — Field Settings → Borders ({@link Theme.BorderWidth}). */
export function resolveFieldBorderWidth(theme: Theme): number {
    return themeNumber(theme.BorderWidth, 1);
}

/** Field opacity — Field Settings → Opacity ({@link Theme.ControlAlpha}). 0 = Transparent. */
export function resolveFieldAlpha(theme: Theme): number {
    return themeNumber(theme.ControlAlpha, 1);
}

export function isFieldTransparent(theme: Theme): boolean {
    return resolveFieldAlpha(theme) === 0;
}

/**
 * "Underline-only" field (DEV-98424): Borders = None *and* Opacity = Transparent, so neither a box
 * border nor a fill delineates the field — a flat bottom rule does instead.
 *
 * Opaque + Borders = None deliberately returns false: the opaque fill is the delineation, so the
 * field draws no border at all. Field-scoped twin of {@link Mixins.backgroundless}, which governs
 * choice/button surfaces (padding, margins) rather than field chrome — keep them separate.
 */
export function usesUnderlineOnlyField(theme: Theme): boolean {
    return resolveFieldBorderWidth(theme) === 0 && isFieldTransparent(theme);
}

/**
 * Borders = None but Opacity = Opaque: the fill alone has to delineate the field, so it must be the
 * field's own {@link Theme.ControlBackground} rather than {@link Theme.InputBackground} — the latter
 * is typically white and vanishes against a near-white page (DEV-98424).
 */
export function usesFilledBorderlessField(theme: Theme): boolean {
    return resolveFieldBorderWidth(theme) === 0 && !isFieldTransparent(theme);
}

export function resolveTextInputBorderColor(theme: Theme, focused?: boolean): string {
    return focused ? theme.PrimaryColor : theme.ControlBorderColor;
}

/**
 * Bottom rule for an underline-only field. Modern theme (DEV-98424): flat light grey in every
 * state, including focus, matching the legacy theme's focus-invariant underline. Legacy theme keeps
 * the primary-on-focus colour it renders today.
 *
 * Returns `""` rather than `undefined` so JSS removes the declaration — a function value of
 * `undefined` is read as a getter and silently leaves the previous value in place (jss.cjs.js:229).
 */
export function resolveTextInputBorderBottom(theme: Theme, focused?: boolean): string {
    if (!usesUnderlineOnlyField(theme)) {
        return "";
    }
    if (useModernInputChrome(theme)) {
        return `${FIELD_UNDERLINE_WIDTH_PX}px solid ${theme.ControlBorderColor}`;
    }
    return `${FIELD_UNDERLINE_WIDTH_PX}px solid ${resolveTextInputBorderColor(theme, focused)}`;
}

/**
 * The same underline as {@link resolveTextInputBorderBottom}, drawn as an inset box-shadow for
 * chrome that also sets the `border` shorthand in the same rule. A CSSOM `setProperty("border", …)`
 * expands to every `border-*` longhand, so a `border-bottom` applied before it is silently erased —
 * and JSS applies function values in an order we do not control. A box-shadow is immune (DEV-98424).
 */
export function resolveFieldUnderlineShadow(theme: Theme, color?: string): string {
    return `inset 0 -${FIELD_UNDERLINE_WIDTH_PX}px 0 0 ${color ?? theme.ControlBorderColor}`;
}

/** Border alpha for read-only combobox chrome (e.g. single-language selector). */
export const SELECT_DISABLED_DISPLAY_BORDER_ALPHA = 0.5;

/** Read-only combobox chrome: muted border via opacity, normal label/icon. */
export function resolveSelectDisabledDisplayBorderColor(theme: Theme): string {
    return alpha(theme.ControlBorderColor, SELECT_DISABLED_DISPLAY_BORDER_ALPHA);
}

/**
 * Field corner radius — Field Settings → Corners ({@link Theme.BorderRadius}), clamped to a pill at
 * the input height. Same formula in both themes, independent of border/background visibility so the
 * focus ring (see {@link resolveTextInputFocusRingBorderRadius}) always gets a real radius.
 */
export function resolveTextInputInnerBorderRadius(theme: Theme): number {
    return Math.min(theme.BorderRadius, theme.InputHeight / 2);
}

/** Matches {@link resolveTextInputBorderColor} so the halo tracks the input border (primary only when active). */
export function resolveTextInputFocusRingColor(theme: Theme, active?: boolean): string {
    return resolveTextInputBorderColor(theme, active);
}

export function resolveTextInputFocusRingBorderRadius(theme: Theme): number {
    return resolveTextInputInnerBorderRadius(theme) + resolveTextInputFocusRingOffset(theme);
}

/**
 * Field fill. A borderless-but-opaque modern field always takes {@link Theme.ControlBackground}, so
 * it stays visible with no border to outline it. Mixins.inputBackgroundColor does this too, but only
 * when the page colour is byte-identical to InputBackground — a page of `#f8f9f9` against an input of
 * `#FFFFFF` missed the match and rendered a white field on an almost-white page (DEV-98424).
 */
export function resolveTextInputBackground(theme: Theme, focused?: boolean): string {
    if (useModernInputChrome(theme) && usesFilledBorderlessField(theme)) {
        const fill = focused
            ? darken(theme.ControlBackground, theme.ActiveHighlightValue)
            : theme.ControlBackground;
        return alpha(fill, resolveFieldAlpha(theme));
    }
    return inputBackgroundColor(theme, focused);
}

export function resolveTextInputForeground(theme: Theme, focused?: boolean): string {
    return foregroundColor(theme, resolveTextInputBackground(theme, focused));
}

export function resolveTextInputBorderWidth(theme: Theme, focused?: boolean): number {
    const width = resolveFieldBorderWidth(theme);
    if (useModernInputChrome(theme) && focused) {
        return resolveEmphasisBorderWidth(width);
    }
    return width;
}

export function resolveTextInputBorder(theme: Theme, focused?: boolean): string {
    // An underline-only field has a 0-width box border, so its focus colour is invisible. Pinning it
    // also keeps the `border` shorthand constant for the sheet's lifetime, which is what stops a JSS
    // update from re-applying it and wiping the `border-bottom` longhands set after it — see the
    // declaration-order note in InputBox.styles.ts.
    const active = usesUnderlineOnlyField(theme) ? false : focused;
    return `${resolveTextInputBorderWidth(theme, active)}px solid ${resolveTextInputBorderColor(theme, active)}`;
}

/** Detached listbox panel radius: combobox radius minus {@link SELECT_LISTBOX_RADIUS_OFFSET_PX}. */
export function resolveSelectListboxBorderRadius(theme: Theme): number {
    return Math.max(0, resolveTextInputInnerBorderRadius(theme) - SELECT_LISTBOX_RADIUS_OFFSET_PX);
}
